import {
  SHStack,
  SHTable,
  SHTableBody,
  SHTableContainer,
  SHTableHead,
  SHTableRow,
  SHTypography,
} from "@components/design-systems";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import {
  CommentFeatureMatchColWidthPercent,
  CommentProductColWidthPercent,
} from "@pages/platform-analysis-v2/_id/constants";
import { RootState } from "@redux/store";
import { isNil } from "lodash";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { SummaryCommentHeaderCell } from "../comments/header-cell";
import { SummaryCommentRow } from "../comments/row";
import { getCommentColWidth } from "../comments/utils";
import { useParams } from "react-router";

export const SummaryPlatformComments = memo(() => {
  //#region Hooks & States
  const { analysisId } = useParams<{ analysisId: string }>();
  const analysisSteps = useSelector(
    (state: RootState) => state.platformAnalysis.analysisSteps,
  );
  const isCompleted = useSelector(
    (state: RootState) => state.platformAnalysis.isCompleted,
  );
  const analysisComments = useSelector(
    (state: RootState) =>
      state.platformAnalysis.platformAnalysis?.summary?.analysisComments,
  );

  const analysisCommentSteps = useMemo(() => {
    return analysisSteps?.steps?.filter((item) => item !== AnalysisStep.Setup);
  }, [analysisSteps]);

  const columnsWidth = useMemo(
    () => getCommentColWidth(analysisCommentSteps?.length ?? 0),
    [analysisCommentSteps],
  );

  //#region Render
  if (isNil(analysisCommentSteps)) return <></>;

  return (
    <SHStack width={"100%"} gap={2}>
      <SHTypography variant="subtitle2">
        Platform specific comments
      </SHTypography>
      <SHTableContainer>
        <SHTable>
          <SHTableHead>
            <SHTableRow>
              <SummaryCommentHeaderCell
                width={`${CommentProductColWidthPercent}%`}
              >
                Platform
              </SummaryCommentHeaderCell>
              <SummaryCommentHeaderCell
                width={`${CommentFeatureMatchColWidthPercent}%`}
              >
                Features match
              </SummaryCommentHeaderCell>
              {analysisCommentSteps?.map((step) => (
                <SummaryCommentHeaderCell key={step} width={`${columnsWidth}%`}>
                  {step}
                </SummaryCommentHeaderCell>
              ))}
            </SHTableRow>
          </SHTableHead>
          <SHTableBody>
            {analysisComments?.map((item, index) => {
              return (
                <SummaryCommentRow
                  key={`commentRow.${item.productId}`}
                  rowPath={`analysisComments.${index}`}
                  commentSettingFieldPath={"specificCommentSetting"}
                  analysisId={analysisId ?? ""}
                  columnWidth={columnsWidth}
                  rowData={item}
                  steps={analysisCommentSteps}
                  isViewMode={!!isCompleted}
                />
              );
            })}
          </SHTableBody>
        </SHTable>
      </SHTableContainer>
    </SHStack>
  );
});
